import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import FormTextField from 'components/IAP/formFields/FormTextField';
import {
  useCreateResourceRequest,
  useResourceRequest,
  type ResourceRequestItem,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import {
  Ics213RrFormBody,
  toDatetimeLocalValue,
} from 'components/IAP/ResourceRequests/ics213rr/Ics213RrFormBody';
import {
  itemDescription,
  normalize213rrPriority,
} from 'components/IAP/ResourceRequests/ics213rr/ics213rrPriority';
import { AddRequestedResource } from 'components/MapPage/CorporateViewTabs/ResourceTab/AddRequestedResource';
import { RequestCard } from 'components/MapPage/CorporateViewTabs/ResourceTab/RequestCard';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import {
  selectUser,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';

type RequestFormValues = {
  name: string;
  request_number?: string;
  timestamp?: string;
};

type TaskAssetRequestDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assetName: string;
  resourceSetId?: string | null;
  resourceRequestId?: string | null;
  positionName?: string;
  onLinked: (resourceRequestId: string) => void;
};

function createdRequestId(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const row = data as Record<string, unknown>;
  const id = row.resourceRequestId ?? row.resource_request_id;
  return typeof id === 'string' ? id : '';
}

function CreateAssetRequest({
  assetName,
  resourceSetId,
  onCreated,
  onCancel,
}: {
  assetName: string;
  resourceSetId?: string | null;
  onCreated: (resourceRequestId: string) => void;
  onCancel: () => void;
}) {
  const createResourceRequest = useCreateResourceRequest();
  const [showAdd, setShowAdd] = useState(true);
  const [editResourceIndex, setEditResourceIndex] = useState<number | null>(
    null
  );
  const [seedName, setSeedName] = useState<string | null>(assetName);
  const [seedResourceSetId, setSeedResourceSetId] = useState(resourceSetId);
  const [lineError, setLineError] = useState('');
  const [customResources, setCustomResources] = useState<ResourceRequestItem[]>(
    []
  );
  const user = useAppSelector(selectUser);
  const form = useForm<RequestFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: assetName,
      request_number: '',
      timestamp: toDatetimeLocalValue(),
    },
  });

  useEffect(() => {
    if (customResources.length === 0) return;
    setSeedName(null);
    setSeedResourceSetId(null);
  }, [customResources.length]);

  const signAndCreate = form.handleSubmit(async (formData) => {
    if (!formData.name?.trim()) {
      form.setError('name', { message: 'Name is required' });
      setLineError('Name is required');
      return;
    }
    const incomplete = customResources.some(
      (resource) =>
        !resource.kind ||
        !resource.type ||
        !resource.quantity ||
        !itemDescription(resource)
    );
    if (customResources.length === 0 || incomplete) {
      setLineError('Each line needs quantity, kind, type, and a description.');
      return;
    }
    setLineError('');
    const signer = user?.user_name || user?.email_address || '';
    const items = customResources.map((resource) => {
      const description = itemDescription(resource);
      return {
        ...resource,
        name: description,
        description,
        priority: normalize213rrPriority(resource.priority) || 'Routine',
        unit: 'unit',
        status: 'pending' as ResourceRequestItem['status'],
      };
    });
    const timestamp = formData.timestamp
      ? new Date(formData.timestamp).toISOString()
      : undefined;
    const created = await createResourceRequest.mutateAsync({
      name: formData.name,
      request_number: formData.request_number?.trim() || undefined,
      timestamp,
      items,
      requestor_name: signer,
      requestor_signature: signer,
    });
    const requestId = createdRequestId(created);
    if (requestId) onCreated(requestId);
  });

  const closeResourceEditor = () => {
    setShowAdd(false);
    setEditResourceIndex(null);
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <div className="tw:flex tw:flex-col tw:gap-3">
          <FormTextField title="Enter Request Name" fieldName="name" />
          <Ics213RrFormBody
            mode="create"
            items={customResources}
            setItems={setCustomResources}
            memberPositions={[]}
            editing
            headerEditable
            signSlotIds={['requestedBy']}
            onSignSlot={() => void signAndCreate()}
            signPending={createResourceRequest.isPending}
            addItemSlot={
              <div className="tw:mb-2 tw:flex tw:justify-end">
                <Button
                  type="button"
                  size="sm"
                  data-testid="ics213rr-add-row"
                  onClick={() => {
                    setEditResourceIndex(null);
                    setShowAdd(true);
                  }}
                >
                  + Add Row
                </Button>
              </div>
            }
            addingItem={showAdd && editResourceIndex === null}
            editingItemIndex={showAdd ? editResourceIndex : null}
            renderItemEditor={({ item, editIndex, logisticsCells }) => (
              <AddRequestedResource
                layout="row"
                setCustomResources={setCustomResources}
                workAssignment={editIndex == null ? seedName : null}
                initialResourceSetId={
                  editIndex == null ? seedResourceSetId : null
                }
                setShowAdd={setShowAdd}
                resourceToEdit={item}
                editIndex={editIndex}
                onClose={closeResourceEditor}
                trailingCells={logisticsCells}
              />
            )}
            onEditItem={(index) => {
              setEditResourceIndex(index);
              setShowAdd(true);
            }}
          />
          {lineError ? (
            <p className="tw:text-sm tw:text-destructive">{lineError}</p>
          ) : null}
          <div className="tw:flex tw:flex-wrap tw:gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

function LinkedAssetRequest({ requestId }: { requestId: string }) {
  const userGuid = useAppSelector(selectUserGuid);
  const { data: roster } = useGetRosterForIncident();
  const memberPositions =
    roster?.find((member) => member.userId === userGuid)?.incidentPositions ||
    [];
  const { data: request, isLoading, isError } = useResourceRequest(requestId);

  if (isLoading) {
    return (
      <p className="tw:text-sm tw:text-muted-foreground">Loading request…</p>
    );
  }
  if (isError || !request) {
    return (
      <p className="tw:text-sm tw:text-destructive">
        Failed to load this resource request.
      </p>
    );
  }

  return (
    <RequestCard
      request={request}
      memberPositions={memberPositions}
      reqCount={{}}
      defaultExpanded
    />
  );
}

export function TaskAssetRequestDialog({
  open,
  onOpenChange,
  assetName,
  resourceSetId,
  resourceRequestId,
  positionName,
  onLinked,
}: TaskAssetRequestDialogProps) {
  const [linkedId, setLinkedId] = useState(resourceRequestId ?? '');
  const backLabel = positionName ? `Back to ${positionName}` : 'Back';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:!fixed tw:!inset-0 tw:!top-0 tw:!left-0 tw:!z-[80] tw:!flex tw:!h-dvh tw:!max-h-none tw:!w-screen tw:!max-w-none tw:!translate-x-0 tw:!translate-y-0 tw:!flex-col tw:!gap-0 tw:!rounded-none tw:!p-0 tw:sm:!max-w-none">
        <DialogHeader className="tw:shrink-0 tw:!flex-row tw:items-center tw:gap-3 tw:border-b tw:border-border tw:px-4 tw:py-3 tw:pr-14">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            <ArrowLeft />
            {backLabel}
          </Button>
          <div className="tw:min-w-0">
            <DialogTitle>ICS 213RR Resource Request</DialogTitle>
            <DialogDescription>
              Asset request for {assetName || 'this asset'}.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4 tw:py-4">
          {linkedId ? (
            <LinkedAssetRequest requestId={linkedId} />
          ) : (
            <CreateAssetRequest
              assetName={assetName}
              resourceSetId={resourceSetId}
              onCancel={() => onOpenChange(false)}
              onCreated={(requestId) => {
                setLinkedId(requestId);
                onLinked(requestId);
              }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
